import { useApiClient } from '@/api/ApiClientProvider'
import type { Ga4ImportSkip, Ga4Property } from '@/api/types'

import { Ga4Error } from './errors'
import { isLive } from './status'
import { useGa4Mutation } from './useGa4Mutation'

function summarise(ran: number, skipped: readonly Ga4ImportSkip[]): string {
  const requested = `Requested for ${ran} ${ran === 1 ? 'property' : 'properties'}`
  if (skipped.length === 0) return `${requested}.`
  const retryAfterSeconds = Math.max(...skipped.map((item) => item.retryAfterSeconds))
  return `${requested}; ${skipped.length} skipped (cooldown, retry in ${retryAfterSeconds}s).`
}

/**
 * Tenant-wide "Run import now": every live property, each on its own ten-minute
 * cooldown (`POST /integrations/ga4/import` with no id). Hidden with no live
 * property — the route's 422 "none" refusal is then unreachable from here, but
 * `Ga4Error`'s generic fallback still words it if it is ever hit some other way.
 */
export function RunAllButton({ properties, email }: { properties: readonly Ga4Property[]; email: string }) {
  const client = useApiClient()
  const runAll = useGa4Mutation({ mutationFn: () => client.runGa4Import() })

  if (!properties.some(isLive)) return null

  const result = runAll.data

  return (
    <div className="ci-integration__run-all">
      <button type="button" className="ci-button" onClick={() => runAll.mutate()} disabled={runAll.isPending}>
        {runAll.isPending ? 'Requesting…' : 'Run import now'}
      </button>
      {result ? (
        <span role="status" className="ci-integration__ok">
          {summarise(result.ran, result.skipped)}
        </span>
      ) : null}
      <Ga4Error error={runAll.error} email={email} fallback="Run import now did not work. Try again in a moment." />
    </div>
  )
}
